import { CheckCircle2, Printer, SearchX } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { ReportStatus } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { Tabs } from '#client/components/ui/Tabs';
import { useCan } from '#client/hooks/useCan';
import { routes } from '#client/lib/routes';
import { useWorklist } from './useWorklist';
import { buildStatusTabs, emptyBody, emptyTitle } from './worklist.logic';
import { buildWorklistColumns } from './worklist-columns';
import { WorklistFilterBar } from './WorklistFilterBar';

/**
 * The lab's operational screen: every report, segmented by status.
 *
 * All filter, sort, tab and page state lives in the URL, so a view is
 * shareable and the back button steps through it. Pagination rather than
 * infinite scroll, because operational data needs jump-to-page, comparison and
 * a stable keyboard order.
 *
 * "Completed" is a first-class tab. Its absence in a competing product is the
 * single most-cited complaint about it — staff scrolling past finished work all
 * day to find what still needs doing.
 */
export function WorklistPage() {
  const navigate = useNavigate();
  const can = useCan();
  const worklist = useWorklist();
  const { filters, data } = worklist;

  return (
    <Page
      title="Worklist"
      subtitle="Every report, by where it is in the lab"
      fullWidth
      primaryAction={
        can('patient.create') ? (
          <Button variant="primary" onClick={() => void navigate(routes.patientNew)}>
            Register patient
          </Button>
        ) : undefined
      }
    >
      {worklist.error && <Banner tone="critical">{worklist.error}</Banner>}

      <Card flush>
        <Tabs
          label="Report status"
          items={buildStatusTabs(data)}
          value={filters.status}
          onChange={(status: ReportStatus) => worklist.setStatus(status)}
        />

        <WorklistFilterBar
          filters={filters}
          onSearchChange={worklist.setSearch}
          onToggle={worklist.toggle}
          onSortChange={worklist.setSort}
        />

        <DataTable
          caption="Worklist"
          columns={buildWorklistColumns(filters.status === 'completed')}
          rows={data?.items ?? []}
          rowKey={(row) => row.id}
          loading={worklist.loading}
          skeletonRows={10}
          onRowClick={(row) => void navigate(routes.report(row.id))}
          emptyState={
            <WorklistEmptyState
              status={filters.status}
              hasFilters={worklist.hasFilters}
              onClearFilters={worklist.clearFilters}
            />
          }
          rowActions={(row) => (
            <Button
              size="sm"
              icon={<Printer />}
              aria-label={`Print reports for ${row.patient.name}`}
              onClick={() => void navigate(routes.patientPrint(row.patient.id))}
            />
          )}
        />

        <Pagination
          page={filters.page}
          limit={worklist.limit}
          count={data?.count ?? 0}
          onPageChange={worklist.setPage}
          label="reports"
        />
      </Card>
    </Page>
  );
}

function WorklistEmptyState({
  status,
  hasFilters,
  onClearFilters,
}: {
  status: ReportStatus;
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  if (hasFilters) {
    return (
      <EmptyState
        icon={<SearchX />}
        title="Nothing matches these filters"
        body="Try clearing the search or the filter chips."
        action={<Button onClick={onClearFilters}>Clear filters</Button>}
      />
    );
  }
  return (
    <EmptyState
      icon={<CheckCircle2 />}
      tone="positive"
      title={emptyTitle(status)}
      body={emptyBody(status)}
    />
  );
}
